import React, { ReactElement, ReactNode, useState } from 'react';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { Button } from '@3.0/Buttons';
import RotatingSpinner from '@components/Blackbird/RotatingSpinner/RotatingSpinner';
import cn from 'utils/cn';

export type TInfoBannerVariant = 'warning' | 'success' | 'error' | 'info' | 'loading';

type Props = {
  label: string | ReactNode;
  variant: TInfoBannerVariant;
  testId?: string;
  className?: string;
  retry?: () => void;
  dismissible?: boolean;
  onDismiss?: () => void;
  actionButtons?: ReactNode[];
};

export const bannerTypeMap = {
  warning: {
    background: 'bg-warning-light',
    iconColor: 'warning-default',
    textColor: 'white',
    Icon: WarningIcon,
    borderColor: 'b--warning-default',
  },
  loading: {
    background: 'bg-warning-light',
    iconColor: 'warning-default',
    textColor: 'white',
    Icon: RotatingSpinner,
    borderColor: 'b--warning-default',
  },
  error: {
    background: 'bg-alert-light',
    iconColor: 'alert-default',
    textColor: 'white',
    Icon: NewReleasesIcon,
    borderColor: 'b--alert-default',
  },
  info: {
    background: 'bg-tints-default-medium-light',
    iconColor: 'warning-default',
    textColor: 'white',
    Icon: NewReleasesIcon,
    borderColor: 'b--nobel',
  },
  success: {
    background: 'bg-ok-light',
    iconColor: 'ok-default',
    textColor: 'white',
    Icon: CheckCircleIcon,
    borderColor: 'b--ok-default',
  },
};

const InfoBanner: React.FC<Props> = ({
  label,
  variant = 'warning',
  testId = '',
  className = '',
  retry,
  dismissible = false,
  onDismiss,
  actionButtons = [],
}): ReactElement<any> | null => {
  const Icon = bannerTypeMap[variant]?.Icon || NewReleasesIcon;
  const [visible, setVisible] = useState(true);
  if (!visible) {
    return null;
  }
  return (
    <section
      className={cn(
        `shade-dark tw:border fw4 tw:flex tw:items-stretch`,
        (dismissible || actionButtons.length > 0) && 'tw:justify-between',
        bannerTypeMap[variant].background,
        bannerTypeMap[variant].borderColor,
        className
      )}
      data-testid={testId}
    >
      <div className={cn('tw:flex tw:py-2 tw:px-6', dismissible && 'tw:pr-2')}>
        <div className="tw:flex tw:self-start">
          <Icon
            style={{ width: '18px', height: '18px' }}
            className={`${bannerTypeMap[variant]?.iconColor || ''}`}
          />
        </div>

        <div className={`tw:text-sm tw:ml-2 tw:text-wrap`}>
          <span className={cn(variant === 'error' && retry && 'tw:mr-1')}>{label}</span>
          {variant === 'error' && retry && (
            <button
              type="button"
              className="tw:p-0 tw:border-0 tw:text-accent-300 tw:cursor-pointer tw:bg-transparent tw:!inline tw:m-0"
              onClick={retry}
            >
              Please try again.
            </button>
          )}
        </div>
      </div>
      <div className="tw:flex">
        {dismissible && (
          <Button
            label="Dismiss"
            onClick={() => {
              setVisible(false);
              onDismiss?.();
            }}
            testId="dismiss-button"
            type="button"
            variant="obscure"
            style={{ fontSize: 12 }}
          />
        )}

        {actionButtons.length > 0 && (
          <div className="tw:flex tw:flex-auto tw:h-full">
            {actionButtons.map((button, index) =>
              button && React.isValidElement(button)
                ? React.cloneElement(button, {
                    key: `action-button-${index}`, // Ensure each button has a unique key
                  })
                : null
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default InfoBanner;
